
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, ChevronRight, ChevronLeft, CheckCircle2 } from 'lucide-react';
import { useToast } from '@/hooks/useToast';
import { db } from '@/lib/firebase';
import { collection, addDoc, doc, updateDoc, serverTimestamp, arrayUnion } from 'firebase/firestore';
import { kidBeltRanks } from '@/types';
import { useSubscriptions } from '@/hooks/useSubscriptions';
import { useClasses } from '@/hooks/useClasses';

interface AddKidDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    parentId: string;
    onSuccess?: () => void;
}

export const AddKidDialog: React.FC<AddKidDialogProps> = ({ open, onOpenChange, parentId, onSuccess }) => {
    const { t } = useTranslation();
    const { success, error: showError } = useToast();
    const { plans, assignSubscription } = useSubscriptions();
    const { classes, enrollUserInClass } = useClasses();

    const [step, setStep] = useState(1); // 1: Profile, 2: Plan/Class
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Step 1: Profile Data
    const [name, setName] = useState('');
    const [age, setAge] = useState('');
    const [belt, setBelt] = useState('White');
    const [giveOwnLogin, setGiveOwnLogin] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    // Step 2: Enrollment Data
    const [selectedPlanId, setSelectedPlanId] = useState<string>('');
    const [selectedClassIds, setSelectedClassIds] = useState<string[]>([]);

    // Derived Data
    const filteredPlans = plans.filter(p => p.isActive && p.isPublic);
    // Filter classes suitable for kids (if 'kids' type exists, or just all for now)
    const kidClasses = classes.filter(c => c.status === 'active' && (c.type === 'kids' || c.name.toLowerCase().includes('kid') || c.name.toLowerCase().includes('teen')));
    // Fallback if no specific kid classes found, show all
    const availableClasses = kidClasses.length > 0 ? kidClasses : classes.filter(c => c.status === 'active');

    // Reset on close
    useEffect(() => {
        if (!open) {
            setStep(1);
            setName('');
            setAge('');
            setBelt('White');
            setGiveOwnLogin(false);
            setEmail('');
            setPassword('');
            setSelectedPlanId('');
            setSelectedClassIds([]);
            setError('');
        }
    }, [open]);

    const handleNext = (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        const ageNum = parseInt(age);
        if (isNaN(ageNum) || ageNum < 3 || ageNum > 18) {
            setError(t('add_kid.age_range_error'));
            return;
        }

        if (giveOwnLogin) {
            if (!email || !password) {
                setError(t('add_kid.email_password_required'));
                return;
            }
            if (password.length < 6) {
                setError(t('validation.password_min'));
                return;
            }
        }

        setStep(2);
    };

    const toggleClassSelection = (classId: string) => {
        setSelectedClassIds(prev =>
            prev.includes(classId)
                ? prev.filter(id => id !== classId)
                : [...prev, classId]
        );
    };

    const handleSubmit = async (keepOpen = false) => {
        setError('');
        setLoading(true);

        try {
            const ageNum = parseInt(age);
            let childId = '';

            // 1. Create User
            if (giveOwnLogin) {
                // Call Cloud Function to create account without logging out parent
                const { httpsCallableFromURL } = await import('firebase/functions');
                const { functions, callableUrl } = await import('@/lib/firebase');

                const createChild = httpsCallableFromURL(functions, callableUrl('createChildAccount'));
                const result = await createChild({
                    email,
                    password,
                    displayName: name,
                    age: ageNum,
                    belt
                });
                childId = (result.data as any).childId;
            } else {
                // Create simple profile directly in Firestore
                const kidData = {
                    displayName: name,
                    age: ageNum,
                    role: 'kid',
                    isKidProfile: true,
                    hasOwnLogin: false,
                    parentId: parentId,
                    belt: belt,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                    status: 'active'
                };
                const docRef = await addDoc(collection(db, 'users'), kidData);
                childId = docRef.id;

                // Denormalize the new kid's UID onto the parent doc's
                // `childrenIds` array. See note on the same write in
                // AddKidsStep.tsx (onboarding flow). Non-blocking.
                try {
                    await updateDoc(doc(db, 'users', parentId), {
                        childrenIds: arrayUnion(childId),
                        updatedAt: serverTimestamp(),
                    });
                } catch (denormErr) {
                    console.warn('Failed to denormalize childrenIds on parent doc:', denormErr);
                }
            }

            // 2. Assign Subscription (if selected)
            if (selectedPlanId) {
                try {
                    await assignSubscription(
                        childId,
                        name,
                        selectedPlanId,
                        'manual', // Default to manual/cash for initial setup or handle differently
                        undefined,
                        undefined, // branchId
                        undefined, // startDate
                        undefined, // assignedCoach
                        true // Skip income record? Maybe false if we want to record "due" amount? Let's say false to track revenue.
                    );
                } catch (subErr) {
                    console.error("Failed to assign subscription:", subErr);
                    // Don't block creation, just warn
                    showError(t('add_kid.subscription_failed'));
                }
            }

            // 3. Enroll in Classes (if selected)
            if (selectedClassIds.length > 0) {
                try {
                    await Promise.all(selectedClassIds.map(classId =>
                        enrollUserInClass(childId, name, classId)
                    ));
                } catch (enrollErr) {
                    console.error("Failed to enroll in classes:", enrollErr);
                    showError(t('add_kid.enrollment_failed'));
                }
            }

            success(t('add_kid.added_success', { name }));

            // Reset logic
            if (onSuccess) onSuccess();

            if (!keepOpen) {
                onOpenChange(false);
            } else {
                // Reset form for next child
                setStep(1);
                setName('');
                setAge('');
                setBelt('White');
                setGiveOwnLogin(false);
                setEmail('');
                setPassword('');
                setSelectedPlanId('');
                setSelectedClassIds([]);
            }

        } catch (err) {
            console.error('Error adding child:', err);
            setError(err instanceof Error ? err.message : t('add_kid.failed_to_add'));
            showError(t('add_kid.failed_to_add'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{t('add_kid.title')}</DialogTitle>
                    <DialogDescription>
                        {step === 1 ? t('add_kid.step1_description') : t('add_kid.step2_description')}
                    </DialogDescription>
                </DialogHeader>

                <div className="py-4">
                    {/* Progress Indicator */}
                    <div className="flex items-center justify-center mb-6 space-x-2">
                        <div className={`h-2.5 w-2.5 rounded-full ${step >= 1 ? 'bg-primary' : 'bg-gray-200'}`} />
                        <div className={`h-1 w-10 ${step >= 2 ? 'bg-primary' : 'bg-gray-200'}`} />
                        <div className={`h-2.5 w-2.5 rounded-full ${step >= 2 ? 'bg-primary' : 'bg-gray-200'}`} />
                    </div>

                    {error && (
                        <div className="mb-4 p-3 text-sm text-red-600 bg-red-50 rounded-md border border-red-200">
                            {error}
                        </div>
                    )}

                    {step === 1 ? (
                        <form id="profile-form" onSubmit={handleNext} className="space-y-4">
                            {/* Child's Name */}
                            <div className="space-y-2">
                                <Label htmlFor="name">{t('add_kid.child_full_name')}</Label>
                                <Input
                                    id="name"
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                    placeholder={t('add_kid.name_placeholder')}
                                    required
                                />
                            </div>

                            {/* Age & Belt Grid */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="age">{t('add_kid.age_required')}</Label>
                                    <Input
                                        id="age"
                                        type="number"
                                        min="3"
                                        max="18"
                                        value={age}
                                        onChange={e => setAge(e.target.value)}
                                        placeholder={t('add_kid.age_placeholder')}
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="belt">{t('add_kid.current_belt')}</Label>
                                    <Select value={belt} onValueChange={setBelt}>
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {kidBeltRanks.map((rank) => (
                                                <SelectItem key={rank} value={rank}>
                                                    {rank}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {/* Optional: Independent Login */}
                            <div className="space-y-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <div className="flex items-center space-x-2">
                                    <Checkbox
                                        id="ownLogin"
                                        checked={giveOwnLogin}
                                        onCheckedChange={(checked) => setGiveOwnLogin(checked === true)}
                                    />
                                    <Label
                                        htmlFor="ownLogin"
                                        className="text-sm font-normal cursor-pointer"
                                    >
                                        {t('add_kid.own_login_label')}
                                    </Label>
                                </div>

                                {giveOwnLogin && (
                                    <div className="space-y-3 pl-6 border-l-2 border-gray-200 ml-1 mt-2">
                                        <div className="space-y-2">
                                            <Label htmlFor="email">{t('common.email')}</Label>
                                            <Input
                                                id="email"
                                                type="email"
                                                value={email}
                                                onChange={e => setEmail(e.target.value)}
                                                placeholder={t('add_kid.email_placeholder')}
                                                required
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="password">{t('auth.password')}</Label>
                                            <Input
                                                id="password"
                                                type="password"
                                                value={password}
                                                onChange={e => setPassword(e.target.value)}
                                                placeholder={t('add_kid.password_min_placeholder')}
                                                required
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </form>
                    ) : (
                        <div className="space-y-6">
                            {/* Plan Selection */}
                            <div className="space-y-3">
                                <Label className="text-base font-semibold">{t('add_kid.select_plan')}</Label>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {filteredPlans.map(plan => (
                                        <div
                                            key={plan.id}
                                            className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${selectedPlanId === plan.id
                                                ? 'border-primary bg-primary/5 shadow-sm'
                                                : 'border-transparent bg-gray-50 hover:bg-gray-100'
                                                }`}
                                            onClick={() => setSelectedPlanId(selectedPlanId === plan.id ? '' : plan.id)}
                                        >
                                            <div className="flex justify-between items-start mb-1">
                                                <span className="font-semibold">{plan.name}</span>
                                                {selectedPlanId === plan.id && <CheckCircle2 className="h-4 w-4 text-primary" />}
                                            </div>
                                            <p className="text-sm text-muted-foreground">{plan.description || t('add_kid.access_to_classes')}</p>
                                            <div className="mt-2 font-bold text-sm">
                                                ${plan.price} <span className="text-xs font-normal text-muted-foreground">{t('add_kid.per_days', { days: plan.durationDays })}</span>
                                            </div>
                                        </div>
                                    ))}
                                    {filteredPlans.length === 0 && (
                                        <p className="text-sm text-gray-400 italic">{t('add_kid.no_public_plans')}</p>
                                    )}
                                </div>
                            </div>

                            {/* Class Selection */}
                            <div className="space-y-3">
                                <Label className="text-base font-semibold">{t('add_kid.enroll_in_classes')}</Label>
                                <div className="space-y-2 max-h-[200px] overflow-y-auto pr-2">
                                    {availableClasses.length > 0 ? availableClasses.map(cls => (
                                        <div
                                            key={cls.id}
                                            className="flex items-center space-x-3 p-2 rounded hover:bg-gray-50 border border-transparent hover:border-gray-100 transition-colors"
                                        >
                                            <Checkbox
                                                id={`cls-${cls.id}`}
                                                checked={selectedClassIds.includes(cls.id)}
                                                onCheckedChange={() => toggleClassSelection(cls.id)}
                                            />
                                            <div className="flex-1">
                                                <div className="flex justify-between">
                                                    <Label htmlFor={`cls-${cls.id}`} className="font-medium cursor-pointer">
                                                        {cls.name}
                                                    </Label>
                                                    <span className="text-xs bg-gray-100 px-2 py-0.5 rounded-full text-gray-600">
                                                        {cls.schedules?.[0]?.dayOfWeek !== undefined
                                                            ? t(`add_kid.days.${['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][cls.schedules[0].dayOfWeek]}`)
                                                            : t('add_kid.flexible')}
                                                        {' '}{cls.schedules?.[0]?.startTime}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-muted-foreground truncate">{cls.coachName}</p>
                                            </div>
                                        </div>
                                    )) : (
                                        <p className="text-sm text-gray-400 italic">{t('add_kid.no_suitable_classes')}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <DialogFooter className="flex justify-between sm:justify-between gap-4">
                    {step === 1 ? (
                        <>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                                disabled={loading}
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button type="submit" form="profile-form">
                                {t('add_kid.next_step')}
                                <ChevronRight className="ml-2 h-4 w-4" />
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => setStep(1)}
                                disabled={loading}
                                className="mr-auto"
                            >
                                <ChevronLeft className="mr-2 h-4 w-4" />
                                {t('common.back')}
                            </Button>
                            <div className="flex gap-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => handleSubmit(true)}
                                    disabled={loading}
                                >
                                    {t('add_kid.save_add_another')}
                                </Button>
                                <Button
                                    type="button"
                                    onClick={() => handleSubmit(false)}
                                    disabled={loading}
                                >
                                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                    {t('add_kid.finish_add_child')}
                                </Button>
                            </div>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
